import React from "react";
import PropTypes from "prop-types";
import { Modal as BootstrapModal } from "react-bootstrap";
import Drawer from "@mui/material/Drawer";
import { isMobile } from "react-device-detect";
import { motion, AnimatePresence } from "framer-motion";

/**
 * DrawerHandle component that renders a swipeable handle for the drawer
 *
 * @component
 * @param {Object} props - The component props
 * @param {Function} props.onHide - Function to handle the hide action
 * @returns {JSX.Element} The rendered handle component
 */
const DrawerHandle = ({ onHide }) => (
    <motion.div
        className="tw-flex tw-justify-center tw-pt-3 tw-pb-2"
        drag="y"
        dragConstraints={{ top: 0, bottom: 60 }}
        onDragEnd={(e, info) => {
            if (info.point.y - info.offset.y > 40) onHide();
        }}
        onClick={onHide}
        title="Close drawer"
        aria-label="Close drawer"
        style={{ cursor: "pointer" }}
        whileHover={{ scale: 1.1 }}
    >
        <div className="tw-w-9 tw-h-1 tw-bg-gray-300 tw-rounded-full" />
    </motion.div>
);

DrawerHandle.propTypes = {
    onHide: PropTypes.func.isRequired,
};

/**
 * ResponsiveWrapper component that conditionally renders either a modal or a drawer
 * based on the device type (mobile or desktop).
 *
 * @component
 * @param {Object} props - The component props.
 * @param {boolean} props.show - Boolean flag to control the visibility of the modal/drawer.
 * @param {Function} props.onHide - Function to handle the hide action for the modal/drawer.
 * @param {React.ReactNode} props.children - The content to be displayed within the modal/drawer.
 * @param {String} props.className - Classname to be used also in PaperProps.
 * @param {boolean} props.animate - Whether to animate the modal/drawer. Defaults to true.
 * @param {String} props.drawerHeight - Height for the mobile drawer. Defaults to '90vh' if not provided.
 * @param {boolean} props.showHandle - Whether to show a horizontal swipe handle at the top of the drawer (mobile only).
 * @returns {JSX.Element} The rendered component.
 */
const ResponsiveWrapper = ({
    show,
    onHide,
    children,
    className,
    animate = true,
    drawerHeight,
    showHandle = false,
    ...props
}) => {
    const slideAnimation = animate
        ? {
              initial: { y: "100%" },
              animate: { y: 0 },
              exit: { y: "100%" },
              transition: {
                  type: "spring",
                  damping: 30, // Damping to reduce bounce
                  stiffness: 200, // Stiffness for smoother motion
                  mass: 0.8, // Mass to make it feel more solid
              },
          }
        : {};

    const fadeAnimation = animate
        ? {
              initial: { opacity: 0, scale: 0.95 },
              animate: { opacity: 1, scale: 1 },
              exit: { opacity: 0, scale: 0.95 },
              transition: {
                  type: "spring",
                  damping: 30,
                  stiffness: 200,
                  mass: 0.8,
              },
          }
        : {};

    const Content = animate ? motion.div : "div";

    return isMobile ? (
        <Drawer
            anchor="bottom"
            open={show}
            onClose={onHide}
            PaperProps={{
                sx: {
                    height: drawerHeight || "90vh",
                    background: animate ? "transparent" : "white",
                    boxShadow: animate ? "none" : undefined,
                    overflow: "hidden", // Prevent any potential overflow during animation
                },
                className,
            }}
            {...props}
            SlideProps={{
                timeout: animate ? 300 : 0,
            }}
        >
            {animate ? (
                <AnimatePresence mode="wait">
                    {show && (
                        <Content
                            {...slideAnimation}
                            className="tw-bg-white tw-h-full tw-rounded-t-[16px] tw-overflow-hidden"
                            style={{ touchAction: "none" }} // Prevents touch events from interfering with animation
                        >
                            {showHandle && <DrawerHandle onHide={onHide} />}
                            {children}
                        </Content>
                    )}
                </AnimatePresence>
            ) : (
                <Content className="tw-bg-white tw-h-full tw-overflow-hidden">
                    {showHandle && <DrawerHandle onHide={onHide} />}
                    {children}
                </Content>
            )}
        </Drawer>
    ) : (
        <BootstrapModal show={show} onHide={onHide} className={className} {...props}>
            {animate ? (
                <AnimatePresence mode="wait">
                    {show && <Content {...fadeAnimation}>{children}</Content>}
                </AnimatePresence>
            ) : (
                <Content>{children}</Content>
            )}
        </BootstrapModal>
    );
};

ResponsiveWrapper.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
    animate: PropTypes.bool,
    drawerHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    showHandle: PropTypes.bool,
};

export default ResponsiveWrapper;
